/* eslint-disable no-console */
import { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";

import MDBox from "components/MDBox";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";

import Footer from "components/Footer";

import { newAlertMessage } from "store/actions/globalAlertActions";

import {
  getAmenityEvents,
  updateAmenityEvent,
  removeAmenityEvent,
} from "store/actions/amenitiesActions";

import { updateServiceEvent, removeServiceEvent } from "store/actions/servicesActions";
import amenityUtils from "../project/components/amenitiesConfig/amenityUtils";
import serviceUtils from "../project/components/servicesConfig/serviceUtils";
import AmenityEventForm from "./AmenityEventForm";
import AmenityEventCalendar from "./AmenityEventsCalendar";

function NewAmenityEvent({
  actNewAlertMessage,
  actAddServiceEvent,
  actRemoveServiceEvent,
  project,
  projectId,
  amenity,
  amenityId,
  apartmentId = null,
  amenityEvents,
  serviceEventsByService,
  services,
  mainUserId,
  actGetAmenityEvents,
  actAddAmenityEvent,
  actRemoveAmenityEvent,
  isProjectManager,
  clientReservations,
}) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [event, setEvent] = useState({});
  const [newEvent, setNewEvent] = useState({});
  const [actionType, setActionType] = useState(false);

  const projectTimeZone = project?.timeZone || "UTC";

  const location = useLocation();

  const calendarInitialDate = useMemo(() => {
    const initialDate = location?.state?.initialDate;

    return amenityUtils.getCalendarInitialDate(initialDate);
  }, [location?.state?.initialDate]);

  useEffect(() => {
    if (!mainUserId) return;
    if (amenityEvents?.[amenityId]) return;

    actGetAmenityEvents();
  }, [mainUserId, amenityId]);

  useEffect(() => {
    const amenityEventsByAmenityId = amenityUtils.getEventsByResourceId(amenityEvents, amenityId);

    setEvents(amenityEventsByAmenityId);
  }, [amenityEvents, amenityId]);

  const handleCloseModal = useCallback(() => {
    setOpen(false);
    setEvent({});
    setNewEvent({});
    setActionType(false);
  }, []);

  const handleEventClick = (clickInfo) => {
    const eventToSelect = clickInfo?.event?.toPlainObject();
    const eventToSelectClientReservationId =
      eventToSelect?.extendedProps?.clientReservationId || eventToSelect?.clientReservationId || "";

    const selectedClientReservation = (clientReservations || []).find(
      (reservation) => reservation?.uid === eventToSelectClientReservationId
    );

    if (isProjectManager && !selectedClientReservation) {
      actNewAlertMessage({
        type: "warning",
        title: "amenity",
        content: "projectManagerUnauthorizedEventEdit",
      });
      return;
    }
    setNewEvent({});
    setActionType("edit");
    setEvent(eventToSelect);
    setOpen(true);
  };

  const handleDateSelect = (selectInfo) => {
    const scheduleValidationError = amenityUtils.isEventInDateRangeByResource({
      start: selectInfo?.start,
      end: selectInfo?.end,
      resource: amenity,
    });

    if (scheduleValidationError === "outside_allowed_hours") {
      actNewAlertMessage({
        type: "warning",
        title: "amenity",
        content: "outside_allowed_hours",
      });
      return;
    }

    const selectedWeekDay = new Date(selectInfo?.start).getDay();

    if (!amenity?.availableDays?.includes(selectedWeekDay)) {
      actNewAlertMessage({
        type: "warning",
        title: "amenity",
        content: "day_not_allowed",
      });
      return;
    }

    setEvent({});
    setActionType("add");
    setNewEvent(selectInfo);
    setOpen(true);
  };

  const handleFormSubmit = useCallback(
    async ({ amenityEvent, associatedServiceEvents }, submittedActionType) => {
      if (!amenityEvent) return;

      const scheduleValidationError = amenityUtils.isEventInDateRangeByResource({
        start: amenityEvent?.start,
        end: amenityEvent?.end,
        durationMinutes: Number(amenity?.amenityDuration),
        resource: amenity,
      });

      if (scheduleValidationError) {
        actNewAlertMessage({
          type: "warning",
          title: "amenity",
          content: scheduleValidationError,
        });
        return;
      }

      const isOverLimitResource = amenityUtils.validateAmenityEventLimit(
        amenityEvent,
        events,
        amenity
      );

      if (isOverLimitResource) {
        actNewAlertMessage({
          type: "warning",
          title: "amenity",
          content: "resourceLimitReached",
        });
        return;
      }

      const previousServiceEvents = event?.extendedProps?.associatedServiceEventIds || {};
      const clientUserId = event?.extendedProps?.clientUserId;
      const clientReservationId = event?.extendedProps?.clientReservationId;
      const eventApartmentId =
        event?.extendedProps?.apartmentId ||
        event?.apartmentId ||
        amenityEvent?.apartmentId ||
        apartmentId;

      const selectedAssociatedServiceIds = new Set(amenityEvent?.associatedServices || []);

      const hasOverLimitAssociatedService = Object.entries(associatedServiceEvents || {}).some(
        ([serviceId, sev]) => {
          const service = services?.[serviceId];
          if (!service) return false;

          const existingEvents = amenityUtils.getEventsByResourceId(
            serviceEventsByService,
            serviceId
          );

          return serviceUtils.validateServiceEventLimit(sev, existingEvents, service);
        }
      );

      if (hasOverLimitAssociatedService) {
        actNewAlertMessage({
          type: "warning",
          title: "service",
          content: "serviceLimitReached",
        });
        return;
      }

      const removeServiceEventAsync = (payload) =>
        new Promise((resolve) => {
          actRemoveServiceEvent(payload, (response) => resolve(response || { ok: false }));
        });

      const updateServiceEventAsync = (payload) =>
        new Promise((resolve) => {
          actAddServiceEvent(payload, (response) => resolve(response || { ok: false }));
        });

      const resolvedAssociatedServiceEventIds = {
        ...(amenityEvent?.associatedServiceEventIds || {}),
      };

      // Remove stale associations first so the amenity saves only currently linked services.
      const staleAssociations = Object.entries(previousServiceEvents).filter(
        ([serviceId]) => !selectedAssociatedServiceIds.has(serviceId)
      );

      staleAssociations.forEach(([serviceId]) => {
        delete resolvedAssociatedServiceEventIds[serviceId];
      });

      const staleAssociationsToRemove = staleAssociations.filter(([, oldServiceEventId]) =>
        Boolean(oldServiceEventId)
      );

      const removeResponses = await Promise.all(
        staleAssociationsToRemove.map(([serviceId, oldServiceEventId]) =>
          removeServiceEventAsync({
            serviceId,
            eventId: oldServiceEventId,
            mainUserId,
            apartmentId: eventApartmentId,
            projectId,
            clientUserId,
            clientReservationId,
          })
        )
      );

      removeResponses.forEach((response) => {
        if (response?.ok) {
          actNewAlertMessage({
            type: "success",
            title: "serviceEvent",
            content: "eventRemoved",
          });
        } else {
          actNewAlertMessage({
            type: "error",
            title: "serviceEvent",
            content: "eventRemoveFailed",
          });
        }
      });

      const associatedEntries = Object.entries(associatedServiceEvents || {});

      const upsertResponses = await Promise.all(
        associatedEntries.map(([serviceId, sev]) =>
          updateServiceEventAsync({
            event: sev,
            projectId,
            mainUserId,
            isAssociated: true,
          }).then((response) => ({ serviceId, sev, response }))
        )
      );

      upsertResponses.forEach(({ serviceId, sev, response }) => {
        const resolvedServiceEventId = response?.event?.id || sev?.id || null;
        resolvedAssociatedServiceEventIds[serviceId] = resolvedServiceEventId;

        console.log("NewAmenityEvent actAddServiceEvent response", { response });
      });

      actAddAmenityEvent(
        {
          event: {
            ...amenityEvent,
            associatedServiceEventIds: resolvedAssociatedServiceEventIds,
          },
          projectId,
          mainUserId,
        },
        (response) => {
          // console.log("NewAmenityEvent actAddAmenityEvent response", {
          //   actionType: submittedActionType,
          //   response,
          // });
          if (response?.ok) {
            actNewAlertMessage({
              type: "success",
              title: "eventAmenity",
              content: submittedActionType === "edit" ? "eventUpdated" : "eventAdded",
            });
          } else {
            actNewAlertMessage({
              type: "error",
              title: "eventAmenity",
              content: submittedActionType === "edit" ? "eventUpdateFailed" : "eventAddFailed",
            });
          }
        }
      );

      handleCloseModal();
    },
    [
      actAddAmenityEvent,
      actAddServiceEvent,
      actNewAlertMessage,
      actRemoveServiceEvent,
      amenity,
      apartmentId,
      event,
      events,
      handleCloseModal,
      mainUserId,
      projectId,
      serviceEventsByService,
      services,
    ]
  );

  const handleFormRemove = useCallback(
    (eventId) => {
      const serviceEventIds = event?.extendedProps?.associatedServiceEventIds || {};

      const clientReservationId =
        event?.extendedProps?.clientReservationId || event?.clientReservationId || "";

      const eventApartmentId =
        event?.extendedProps?.apartmentId || event?.apartmentId || apartmentId;

      Object.entries(serviceEventIds).forEach(([serviceId, sevId]) => {
        if (!sevId) return;

        actRemoveServiceEvent({
          serviceId,
          eventId: sevId,
          mainUserId,
          apartmentId: eventApartmentId,
          projectId,
          clientReservationId,
        });
      });

      actRemoveAmenityEvent(
        {
          amenityId,
          eventId,
          mainUserId,
          apartmentId: eventApartmentId,
          projectId,
          clientReservationId,
        },
        (resp) => {
          // console.log("NewAmenityEvent actRemoveAmenityEvent response", { resp });
          if (resp?.ok) {
            actNewAlertMessage({
              type: "success",
              title: "eventAmenity",
              content: "eventRemoved",
            });
          } else {
            actNewAlertMessage({
              type: "error",
              title: "eventAmenity",
              content: "eventRemoveFailed",
            });
          }
        }
      );

      handleCloseModal();
    },
    [
      actRemoveAmenityEvent,
      actRemoveServiceEvent,
      amenityId,
      apartmentId,
      event,
      handleCloseModal,
      mainUserId,
      projectId,
    ]
  );

  return (
    <DashboardLayout>
      <Modal open={open} onClose={handleCloseModal} style={{ overflow: "scroll" }}>
        <MDBox
          mt={3}
          sx={12}
          sm={8}
          style={{
            position: "absolute",
            width: "65vw",
            left: "50%",
            transform: "translate(-50%, 0%)",
          }}
        >
          <AmenityEventForm
            amenity={amenity}
            event={event}
            newEvent={newEvent}
            actionType={actionType}
            projectId={projectId}
            allEvents={events}
            actNewAlertMessage={actNewAlertMessage}
            onSubmit={handleFormSubmit}
            onRemove={handleFormRemove}
          />
        </MDBox>
      </Modal>

      <DashboardNavbar
        title={amenity?.name}
        route={`/projects/${project?.name}/${amenity?.name}`}
        firstLink="/projects/"
        secondLink={`/projects/${projectId}`}
      />

      <MDBox pt={6} pb={3}>
        <AmenityEventCalendar
          handleDateSelect={handleDateSelect}
          handleEventClick={handleEventClick}
          events={events}
          amenity={amenity}
          projectTimeZone={projectTimeZone}
          initialDate={calendarInitialDate}
        />
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

NewAmenityEvent.defaultProps = {
  amenity: {},
  projectId: "",
  amenityId: "",
  project: { name: "" },
  apartmentId: "",
  amenityEvents: {},
  services: {},
  serviceEventsByService: {},
  mainUserId: "",
  isProjectManager: false,
  clientReservations: [],
};

NewAmenityEvent.propTypes = {
  actNewAlertMessage: PropTypes.func.isRequired,
  actAddServiceEvent: PropTypes.func.isRequired,
  actRemoveServiceEvent: PropTypes.func.isRequired,
  actGetAmenityEvents: PropTypes.func.isRequired,
  actAddAmenityEvent: PropTypes.func.isRequired,
  actRemoveAmenityEvent: PropTypes.func.isRequired,

  amenity: PropTypes.object,
  amenityId: PropTypes.string,
  project: PropTypes.object,
  projectId: PropTypes.string,
  apartmentId: PropTypes.string,
  amenityEvents: PropTypes.object,
  services: PropTypes.object,
  mainUserId: PropTypes.string,
  serviceEventsByService: PropTypes.object,
  isProjectManager: PropTypes.bool,
  clientReservations: PropTypes.array,
};

const mapStateToProps = (state, { uid, projectId, apartmentId = null }) => {
  const {
    amenitiesReducer,
    projectsReducer,
    servicesReducer,
    mainUserReducer,
    clientReservationsReducer,
  } = state;
  const { isProjectManager, projects: projectManagerProjects } = mainUserReducer || {};

  const clientReservationsObj = clientReservationsReducer?.clientReservations || {};

  let clientReservations = Object.values(clientReservationsObj).flatMap((item) =>
    item && typeof item === "object" ? Object.values(item) : []
  );

  if (isProjectManager) {
    clientReservations = clientReservations.filter(
      (reservation) => projectManagerProjects?.[reservation?.projectId]
    );
  }
  return {
    amenity: amenitiesReducer.amenities[uid],
    amenityId: uid,
    projectId,
    project: projectsReducer[projectId],
    apartmentId,
    amenityEvents: amenitiesReducer.amenityEvents,
    mainUserId: mainUserReducer?.uid,
    services: servicesReducer.services,
    serviceEventsByService: servicesReducer.serviceEvents,
    isProjectManager,
    clientReservations,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actNewAlertMessage: (data) => dispatch(newAlertMessage(data)),

  actAddServiceEvent: (dataToSend, cb) => dispatch(updateServiceEvent({ dataToSend, cb })),
  actRemoveServiceEvent: (dataToSend, cb) => dispatch(removeServiceEvent({ dataToSend, cb })),

  actGetAmenityEvents: () => dispatch(getAmenityEvents()),
  actAddAmenityEvent: (dataToSend, cb) => dispatch(updateAmenityEvent({ dataToSend, cb })),
  actRemoveAmenityEvent: (dataToSend, cb) => dispatch(removeAmenityEvent({ dataToSend, cb })),
});

const Connected = connect(mapStateToProps, mapDispatchToProps)(NewAmenityEvent);

function Wrapper(props) {
  const { uid, projectId, apartmentId } = useParams();
  if (apartmentId)
    return <Connected uid={uid} projectId={projectId} apartmentId={apartmentId} {...props} />;
  return <Connected uid={uid} projectId={projectId} {...props} />;
}

export default Wrapper;
